<template>
  <div
    class="vcore_popover"
    :class="[
      `vcore_popover-${currentPlacement}`,
      { vcore_hidden: !scopedShow },
      { 'popover-mobile-fix': mobileView },
    ]"
    role="tooltip"
    :id="id"
    :ref="id"
  >
    <div class="vcore_popover-arrow" x-arrow></div>
    <i class="vcore_popover-icon vcore_icon icon-error"></i>
    <i class="vcore_popover-close vcore_icon icon-close" @click="close"></i>
    <h3 v-if="titleClean" class="vcore_popover-title">{{ titleClean }}</h3>
    <div v-if="contentClean" class="vcore_popover-content">
      {{ contentClean }}
      <slot></slot>
    </div>
    <img
      v-if="content.image"
      :src="require(`@Shared/assets/images/${content.image}`)"
    />
  </div>
</template>

<script>
import Popper from "popper.js";
import Uuid from "@Shared/utils/Uuid";
import { resolvePropertyByString } from "@Shared/utils/Object";

export default {
  name: "Popover",
  props: {
    target: {
      required: true,
      type: [String, HTMLElement],
    },
    id: {
      // ID to use for tooltip element
      // If not provided on will automatically be generated
      type: String,
      default: "popover_" + Uuid(),
    },
    placement: {
      type: String,
      default: "top",
    },
    title: {
      type: [String, Object],
      default: null,
    },
    content: {
      type: [String, Object],
      default: null,
    },
    show: {
      type: Boolean,
      default: false,
    },
    turnOffTranslate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      popper: null,
      currentPlacement: this.placement,
      scopedShow: this.show,
      timeoutTriggered: false,
      mobileView: this.turnOffTranslate,
      screenSize: window.innerHeight,
    };
  },
  watch: {
    scopedShow() {
      this.$emit("update:show", this.scopedShow);
    },
    target() {
      this.render();
    },
    show() {
      this.render();
    },
  },
  computed: {
    titleClean() {
      if (!this.title) {
        return null;
      }

      let title = null;
      if (this.title.type === "store") {
        title = resolvePropertyByString(this.$store.state, this.title.value);
      } else if (this.title.type === "string") {
        title = this.title.value;
      }

      if (!title) {
        return this.title.default || "";
      }
      return title;
    },
    contentClean() {
      if (!this.content) {
        return null;
      }

      let content = null;
      if (this.content.type === "store") {
        content = resolvePropertyByString(
          this.$store.state,
          this.content.value
        );
      } else if (this.content.type === "string") {
        content = this.content.value;
      }
      if (!content) {
        return this.content.default || "";
      }
      return content;
    },
  },
  methods: {
    isElement(el) {
      return typeof HTMLElement === "object"
        ? el instanceof HTMLElement //DOM2
        : el &&
            typeof el === "object" &&
            el !== null &&
            el.nodeType === 1 &&
            typeof el.nodeName === "string";
    },
    onCreate(popperData) {
      if (popperData.originalPlacement !== popperData.placement) {
        this.handlePopperPlacementChange(popperData);
      }
    },
    onUpdate(popperData) {
      this.handlePopperPlacementChange(popperData);
    },
    handlePopperPlacementChange(popperData) {
      this.currentPlacement = popperData.placement;
    },
    getTargetElement() {
      // look for target in document
      if (this.target) {
        if (this.isElement(this.target)) {
          return this.target;
        } else if (document.querySelectorAll(this.target).length > 0) {
          return document.querySelectorAll(this.target)[0];
        } else {
          console.error("Popover: Couldn't find target element");
        }
      }
    },
    close() {
      this.scopedShow = false;
    },
    toggle(e) {
      e.preventDefault();
      if (this.timeoutTriggered) {
        this.scopedShow = !this.scopedShow;
      }
    },
    handleClickOffs(event) {
      let targetElement = this.getTargetElement();
      if (
        !this.$refs[this.id].contains(event.target) &&
        !targetElement.contains(event.target) &&
        this.scopedShow
      ) {
        this.close();
      }
    },
    render() {
      let targetElement = this.getTargetElement();

      // if target exists init popover
      if (targetElement) {
        this.popper = new Popper(targetElement, this.$refs[this.id], {
          placement: this.placement,
          modifiers: {
            arrow: {
              enabled: true,
            },
          },
          onCreate: this.onCreate,
          onUpdate: this.onUpdate,
        });
        targetElement.addEventListener("click", this.toggle);
        document.body.addEventListener("click", this.handleClickOffs);

        setTimeout(() => {
          this.popper.update();
          this.timeoutTriggered = true;
        }, 1000);
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.render();
    });
    if (this.turnOffTranslate) {
      window.addEventListener("resize", () => {
        this.screenSize = window.innerWidth;
        if (this.screenSize < 995) {
          this.mobileView = true;
        } else {
          this.mobileView = false;
        }
      });
    }
  },
  beforeDestroy() {
    let targetElement = this.getTargetElement();
    if (targetElement) {
      targetElement.removeEventListener("click", this.toggle);
    }
    document.body.removeEventListener("click", this.handleClickOffs);
  },
};
</script>

<style lang="scss" scoped>
img {
  margin-top: 10px;
}

.popover-mobile-fix {
  transform: translate(10px, 10px) !important;
}
</style>
